/**
 * The agents you have made — the same shape as the graphs index, on purpose.
 *
 * An agent is a saved way of asking: a model, the knowledge graphs it may read,
 * and how it should behave. Making one is two decisions, so the dialog asks for
 * two things and puts everything else behind "More settings" with a working
 * default already in it.
 *
 * Cards rather than a table, because the thing you are picking between is a
 * *character* — what it knows and which model answers — and those do not read
 * as columns.
 */

import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { EmptyState, ErrorNotice, Loading, Modal, relativeTime } from '@/shared/components'
import { useToast } from '@/shared/components/toast'
import { Chip, SearchBox } from '@/features/brain/components'
import { RowMenu } from '@/features/brain/components/RowMenu'
import { AgentDialog } from './AgentDialog'
import { useAgents, useDeleteAgent } from './hooks'
import type { Agent } from './types'
import './agents.css'
import { Constellation } from '@/shared/components/Constellation'

export function AgentsPage() {
  const agents = useAgents()
  const remove = useDeleteAgent()
  const navigate = useNavigate()
  const toast = useToast()

  const [search, setSearch] = useState('')
  const [creating, setCreating] = useState(false)
  const [editing, setEditing] = useState<Agent | null>(null)
  const [deleting, setDeleting] = useState<Agent | null>(null)

  const rows = useMemo(() => {
    const all = agents.data ?? []
    const needle = search.trim().toLowerCase()
    if (!needle) return all
    return all.filter(
      (a) =>
        a.name.toLowerCase().includes(needle) ||
        (a.description ?? '').toLowerCase().includes(needle) ||
        a.model.toLowerCase().includes(needle) ||
        a.graphs.some((g) => (g.name ?? '').toLowerCase().includes(needle)),
    )
  }, [agents.data, search])

  return (
    <div className="cp-screen">
      <div className="cp-scroll">
        <div className="cp-toolbar hero">
          <Constellation />
          <h1>Agents</h1>
          {agents.data && (
            <Chip>
              {agents.data.length} agent{agents.data.length === 1 ? '' : 's'}
              {rows.length !== agents.data.length && ` · ${rows.length} shown`}
            </Chip>
          )}
          <span className="cp-spacer" />
          {(agents.data?.length ?? 0) > 0 && (
            <SearchBox value={search} onChange={setSearch} placeholder="Search agents…" />
          )}
          <button className="btn btn-primary" onClick={() => setCreating(true)}>
            New agent
          </button>
          <div className="sub2">
            Give an agent knowledge graphs and it answers from those and nothing else — a
            question they cannot evidence is refused rather than guessed at. Give it none and
            it answers openly, and says so.
          </div>
        </div>

        {agents.isLoading && <Loading label="Loading agents…" />}
        {agents.error && <ErrorNotice error={agents.error} onRetry={agents.refetch} />}

        {agents.data?.length === 0 && (
          <EmptyState
            title="No agents yet"
            action={
              <button className="btn btn-primary" onClick={() => setCreating(true)}>
                Create an agent
              </button>
            }
          >
            Pick a model, point it at a knowledge graph, and ask it questions. It reads only
            that graph, cites the records it used, and says so plainly when the answer is not
            there. Make as many as you like — one per domain, or one per model to compare.
          </EmptyState>
        )}

        {agents.data && agents.data.length > 0 && rows.length === 0 && (
          <EmptyState title="Nothing matches that search">
            Try a different term, or clear the box.
          </EmptyState>
        )}

        {rows.length > 0 && (
          <div className="ag-grid">
            {rows.map((agent) => (
              <article
                className="ag-card"
                key={agent.assistant_id}
                data-clickable="true"
                onClick={() => navigate(`/agents/${agent.assistant_id}`)}
                style={
                  agent.accent
                    ? ({ '--ag-accent': agent.accent } as React.CSSProperties)
                    : undefined
                }
              >
                <header className="ag-cardhead">
                  <span className="ag-avatar" aria-hidden="true">
                    {agent.avatar || '◈'}
                  </span>
                  <div className="ag-cardname">
                    <h3>{agent.name}</h3>
                    <span className="mono small faint">{agent.model}</span>
                  </div>
                  <span
                    className="cp-spacer"
                    onClick={(e) => e.stopPropagation()}
                    role="presentation"
                  />
                  <span onClick={(e) => e.stopPropagation()} role="presentation">
                    <RowMenu
                      items={[
                        { label: 'Open', onSelect: () => navigate(`/agents/${agent.assistant_id}`) },
                        { label: 'Configure', onSelect: () => setEditing(agent) },
                        {
                          label: 'Delete',
                          danger: true,
                          onSelect: () => setDeleting(agent),
                        },
                      ]}
                    />
                  </span>
                </header>

                <p className="ag-carddesc">
                  {agent.description || 'No description.'}
                </p>

                <div className="ag-cardgraphs">
                  {agent.graphs.length === 0 && <Chip tone="plain">open · no graph</Chip>}
                  {agent.graphs.map((graph) => (
                    <Chip key={graph.graph_id} tone={graph.name ? 'plain' : 'err'}>
                      {graph.name ?? 'a graph that no longer exists'}
                    </Chip>
                  ))}
                </div>

                <footer className="ag-cardfoot small faint">
                  <span>
                    {agent.grounding === 'strict' ? 'Answers only from the graph' : 'Graph first, then general knowledge'}
                  </span>
                  <span className="cp-spacer" />
                  {agent.thread_count ? (
                    <span>
                      {agent.thread_count} conversation{agent.thread_count === 1 ? '' : 's'}
                    </span>
                  ) : (
                    <span>Never used</span>
                  )}
                  <span>· {relativeTime(agent.updated_at)}</span>
                </footer>
              </article>
            ))}
          </div>
        )}
      </div>

      {creating && (
        <AgentDialog
          onClose={() => setCreating(false)}
          onSaved={(agent) => {
            setCreating(false)
            navigate(`/agents/${agent.assistant_id}`)
          }}
        />
      )}

      {editing && (
        <AgentDialog
          agent={editing}
          onClose={() => setEditing(null)}
          onSaved={() => setEditing(null)}
        />
      )}

      {deleting && (
        <Modal
          title={`Delete ${deleting.name}?`}
          onClose={() => setDeleting(null)}
          footer={
            <>
              <button className="btn" onClick={() => setDeleting(null)}>
                Cancel
              </button>
              <button
                className="btn btn-danger"
                disabled={remove.isPending}
                onClick={async () => {
                  try {
                    await remove.mutateAsync(deleting.assistant_id)
                    toast.success('Agent deleted', deleting.name)
                    setDeleting(null)
                  } catch (error) {
                    toast.error(error)
                  }
                }}
              >
                {remove.isPending ? 'Deleting…' : 'Delete agent'}
              </button>
            </>
          }
        >
          <p className="small">
            Its {deleting.thread_count ?? 0} conversation
            {(deleting.thread_count ?? 0) === 1 ? '' : 's'} go with it. The knowledge graphs
            it reads are untouched — an agent is a way of asking, not the data.
          </p>
        </Modal>
      )}
    </div>
  )
}
